import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';

const VisionPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-gray-800">Vision</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          {/* Title */}
          <div className="text-center mb-12 pb-8 border-b border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <Target className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Vision</h1>
          </div>

          {/* Content Sections */}
          <div className="prose prose-gray max-w-none">
            {/* Why SnowExchange exists */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Why SnowExchange exists</h2>
              <p className="text-gray-700 leading-relaxed">
                The ServiceNow ecosystem was built to automate, structure, and optimize how work gets done. Yet, when it comes to staffing and resource allocation, many organizations still rely on slow, fragmented, and manual processes. Projects struggle to find the right expertise at the right time. Qualified consultants sit on the bench while demand exists elsewhere. Partners operate in silos, even though collaboration would benefit everyone. SnowExchange was created to address this contradiction.
              </p>
            </section>

            {/* Where it comes from */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Where it comes from</h2>
              <p className="text-gray-700 leading-relaxed">
                SnowExchange was born from direct, hands-on experience within the ServiceNow ecosystem — working closely with consulting firms, partners, freelancers, and clients across multiple regions. Over time, one pattern became clear: the problem was not a lack of talent, but a lack of visibility, coordination, and trust at scale. The ecosystem already had the skills. What it was missing was a modern way to connect them.
              </p>
            </section>

            {/* What SnowExchange is */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What SnowExchange is</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                SnowExchange is a B2B platform designed to facilitate professional collaboration around ServiceNow delivery. It acts as a neutral operating layer where companies and professionals can:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>make their resource needs visible,</li>
                <li>share availability and expertise,</li>
                <li>collaborate across organizational boundaries,</li>
                <li>accelerate staffing and delivery decisions.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                SnowExchange does not replace human judgment. It supports it with structure, data, and intelligent tooling.
              </p>
            </section>

            {/* A collaborative mindset */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">A collaborative mindset</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                SnowExchange is built on a simple belief: collaboration beats competition when it comes to delivery efficiency. The platform is not about disintermediating partners or commoditizing consultants. It is about enabling better outcomes for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>projects,</li>
                <li>delivery teams,</li>
                <li>consultants,</li>
                <li>and ultimately, end clients.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                By reducing friction and improving alignment, everyone wins.
              </p>
            </section>

            {/* Human and AI delivery, together */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Human and AI delivery, together</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                SnowExchange embraces a future where human expertise and AI capabilities coexist. The platform is designed to support:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>human consultants,</li>
                <li>AI-augmented delivery models,</li>
                <li>and, over time, autonomous agents assisting with specific tasks.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                AI is used as an enabler — not a decision-maker — helping teams move faster and focus on what matters most.
              </p>
            </section>

            {/* The long-term vision */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">The long-term vision</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The ambition behind SnowExchange is clear: <strong>To become the modern infrastructure for ServiceNow staffing and delivery.</strong>
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                At a macro level, SnowExchange aims to connect the broader ServiceNow ecosystem — partners, consulting firms, freelancers, and clients — into a more fluid and efficient network.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                At a micro level, the platform is designed to help large organizations and partner groups manage resource allocation across internal entities, projects, and teams.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Over time, SnowExchange may evolve toward deeper integration with the ServiceNow platform itself, aligning naturally with its strategy, values, and ecosystem.
              </p>
            </section>

            {/* Building step by step */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Building step by step</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                SnowExchange is an early-stage project. It is built incrementally, with a strong focus on:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>real-world use cases,</li>
                <li>practical value,</li>
                <li>and long-term sustainability.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                There are no shortcuts, no inflated promises, and no artificial hype. Only a clear direction and the patience to build something that lasts.
              </p>
            </section>

            {/* Looking forward */}
            <section className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Looking forward</h2>
              <p className="text-gray-700 leading-relaxed">
                SnowExchange is not just a platform. It is a vision for how professional collaboration can work better in a complex, global ecosystem. Open, structured, and aligned with the way modern delivery should function.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 mt-12 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} SnowExchange. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VisionPage;
